/* eslint-disable max-lines */
import { ArrowLongRightIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SourceBadge from "../../components/customer-feedback/SourceBadge";
import StatusBadge from "../../components/customer-feedback/StatusBadge";
import UpdateStatusModal from "../../components/customer-feedback/UpdateStatusModal";
import Back from "../../components/ui/Back";
import Box from "../../components/ui/Box";
import Button from "../../components/ui/Button";
import SpinnerLoader from "../../components/ui/SpinnerLoader";
import SuperAdmin from "../../components/users/permissions/SuperAdmin";
import {
  FeedbackStatus,
  ICustomerFeedback
} from "../../interfaces/endpoints/ICustomerFeedback";
import { getFeedbackById } from "../../services/CustomerFeedback.service";
import { formatReason, formatRouteName } from "../../utils/customerFeedback/labels";

// Detail view for a single feedback record. Read-only except for
// the Status field, which is editable through the same modal used
// in the list table.
//
// Layout mirrors the FormWrapper-based pages (e.g. AddPromotionCode):
// a full-width / full-height Box is the page shell, the title sits
// on a full-width header strip, and the actual content is centred
// inside a max-w-3xl column so reading lines don't stretch across
// the whole monitor.
const CustomerFeedbackDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [record, setRecord] = useState<ICustomerFeedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getFeedbackById(parseInt(id, 10))
      .then((res) => {
        setRecord(res.data as unknown as ICustomerFeedback);
        setError(null);
      })
      .catch(() => {
        setError("Ky regjistrim nuk u ngarkua dot.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <SpinnerLoader />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <p className="text-sm text-red-600 mb-4">
          {error ?? "Regjistrimi nuk u gjet."}
        </p>
        <Back to="/customer-feedback" title="Kthehu te lista" />
      </div>
    );
  }

  const handleStatusUpdated = (newStatus: FeedbackStatus) => {
    setRecord({ ...record, status: newStatus });
  };

  // Section-visibility helpers. We hide entire sections when they
  // have no meaningful data, so the page doesn't show a wall of
  // "-" placeholders for a record that only carried, e.g., a route
  // + reason chips.
  const hasReasons = (record.reasons?.length ?? 0) > 0;
  const hasMessage = !!record.message && record.message.trim().length > 0;
  const hasContent = hasReasons || hasMessage;
  const hasTripDetails =
    !!record.operatorName ||
    !!record.tripDepartureTime ||
    !!record.tripArrivalTime ||
    !!record.lineType;

  const insertDate = record.auditAuthor?.insertDate;
  const updateDate = record.auditAuthor?.updateDate;

  return (
    <>
      {/* Top action row — sits outside the Box, same as the Back
          link on the FormWrapper pages. */}
      <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
        <Back
          to="/customer-feedback"
          title="Kthehu te lista"
          state={["Customer Feedback", "Detajet"]}
        />
        <SuperAdmin>
          <Button onClick={() => setStatusModalOpen(true)}>
            Ndrysho statusin
          </Button>
        </SuperAdmin>
      </div>

      <Box className="h-full p-0">
        <div className="flex flex-col h-full">
          {/* Title strip — full width, matches FormWrapper's title
              treatment (`text-xl py-3 px-5 border-b`). The submission
              timestamp and badges share this row to give the record
              identity at a glance. */}
          <div className="border-b py-3 px-5 flex flex-wrap items-center
            justify-between gap-3">
            <div>
              <h1 className="text-xl text-tableColor">Reagim #{record.id}</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {insertDate
                  ? `Dërguar më ${dayjs(insertDate).format(
                      "DD MMM YYYY · HH:mm"
                    )}`
                  : "—"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SourceBadge source={record.source} />
              <StatusBadge status={record.status} />
            </div>
          </div>

          {/* Scrollable area takes up the rest of the Box height; the
              actual content lives in a bordered card centered inside
              it. Same idiom FormWrapper uses for its centered form
              body, just with a wider cap (`3xl`) and a visible border
              so the reading column stays contained. */}
          <div className="flex-1 overflow-auto p-4">
            <div className="max-w-xl mx-auto w-full border rounded-lg
              p-5 space-y-6">
            {/* Route — the visual anchor BD scans for. */}
            <section>
              <div className="flex flex-wrap items-center gap-3 text-xl
                font-semibold text-tableColor">
                <span>{formatRouteName(record.routeOrigin)}</span>
                <ArrowLongRightIcon className="w-6 h-6 text-gray-400 shrink-0" />
                <span>{formatRouteName(record.routeDestination)}</span>
              </div>
              {(record.searchDate || record.resultsCount !== null) && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4
                  gap-y-1 text-sm text-gray-600">
                  {record.searchDate && (
                    <span>
                      <span className="text-gray-500">Data e kërkimit · </span>
                      <span className="text-tableColor">
                        {record.searchDate}
                      </span>
                    </span>
                  )}
                  {record.searchDate && record.resultsCount !== null && (
                    <span className="text-gray-300">·</span>
                  )}
                  {record.resultsCount !== null && (
                    <span>
                      <span className="text-gray-500">Rezultate · </span>
                      <span className="text-tableColor">
                        {record.resultsCount}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </section>

            {/* Content — reasons + free-text message. Promoted high
                in the page because this is the actual user feedback
                BD cares about. Hidden when the record has neither. */}
            {hasContent && (
              <section className="space-y-4 pt-6 border-t">
                {hasReasons && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      Arsyet
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {record.reasons.map((r, i) => (
                        <span
                          key={`${r}-${i}`}
                          title={r}
                          className="inline-block rounded-full bg-primary/10
                            text-primary px-2.5 py-1 text-xs font-medium"
                        >
                          {formatReason(r)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {hasMessage && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      Mesazhi
                    </p>
                    <div className="rounded-md bg-gray-50 border
                      border-gray-200 px-4 py-3">
                      <p className="whitespace-pre-wrap text-sm
                        text-tableColor leading-relaxed">
                        {record.message}
                      </p>
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Trip details — only present for ReportLine submissions.
                Whole section is skipped when every field is empty. */}
            {hasTripDetails && (
              <section className="pt-6 border-t">
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-y-4
                  gap-x-6">
                  {record.operatorName && (
                    <Field label="Operatori">{record.operatorName}</Field>
                  )}
                  {record.tripDepartureTime && (
                    <Field label="Ora e nisjes">
                      {record.tripDepartureTime}
                    </Field>
                  )}
                  {record.tripArrivalTime && (
                    <Field label="Ora e mbërritjes">
                      {record.tripArrivalTime}
                    </Field>
                  )}
                  {record.lineType && (
                    <Field label="Tipi i linjës">{record.lineType}</Field>
                  )}
                </dl>
              </section>
            )}

            {/* Submission metadata — contact + lifecycle, lowest
                visual weight: BD only consults it when replying or
                auditing. Two columns so values don't feel stranded
                in whitespace on a wide monitor. */}
            <section className="pt-6 border-t">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                <Field label="Email">
                  {record.email ? (
                    <a
                      href={`mailto:${record.email}`}
                      className="text-primary hover:underline break-all"
                    >
                      {record.email}
                    </a>
                  ) : (
                    <span className="italic text-gray-400">Anonim</span>
                  )}
                </Field>
                <Field label="ID e udhëtimit">
                  {record.tripId !== null && record.tripId !== undefined ? (
                    <Link
                      to={`/trips/edit/${record.tripId}`}
                      className="text-primary hover:underline"
                    >
                      {record.tripId}
                    </Link>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </Field>
                <Field label="Dërguar më">
                  {insertDate
                    ? dayjs(insertDate).format("DD MMM YYYY · HH:mm")
                    : "—"}
                </Field>
                <Field label="Përditësuar së fundmi">
                  {updateDate ? (
                    <>
                      {dayjs(updateDate).format("DD MMM YYYY · HH:mm")}
                      {record.auditAuthor?.updatedByName ? (
                        <span className="text-gray-500">
                          {" · nga "}
                          {record.auditAuthor.updatedByName}
                          {record.auditAuthor.updatedByLastName
                            ? ` ${record.auditAuthor.updatedByLastName}`
                            : ""}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </Field>
              </dl>
            </section>
            </div>
          </div>
        </div>
      </Box>

      <UpdateStatusModal
        show={statusModalOpen}
        feedbackId={record.id}
        currentStatus={record.status}
        onClose={() => setStatusModalOpen(false)}
        onUpdated={handleStatusUpdated}
      />
    </>
  );
};

interface FieldProps {
  label: string;
  className?: string;
  children: React.ReactNode;
}

const Field: FC<FieldProps> = ({ label, className, children }) => (
  <div className={className}>
    <dt className="text-xs font-medium text-gray-500 mb-1">{label}</dt>
    <dd className="text-sm text-tableColor">{children}</dd>
  </div>
);

export default CustomerFeedbackDetail;
